import {animateByClass} from "../../../../Library/Animation.js";
import {renderSamplePreview} from "../../TemplateFields";
import {buildOptionChip, buildTagChip, hasGroupAttributes, selectedOption} from "../../Preview/Selects.js";

export const updateActionLabel = (preview) => {
    const label = preview?.querySelector('.actions .action-label');

    if (!label) {
        return;
    }

    const actionCount = preview.querySelectorAll('.actions ol li').length;
    const hasFilters  = preview.querySelector('.filter-preview-groups')?.children.length > 0;

    label.hidden      = actionCount === 0 || !hasFilters;
    label.textContent = actionCount > 1 ? label.dataset.plural : label.dataset.singular;
};

const applySpanValue = (span, input) => {
    if (!span) {
        return;
    }

    if (input.nodeName === 'SELECT') {
        const selected = selectedOption(input);

        if (selected?.dataset.tagColor) {
            span.replaceChildren(buildTagChip(selected));

            return;
        }

        if (selected?.value && hasGroupAttributes(selected)) {
            span.replaceChildren(buildOptionChip(input, selected));

            return;
        }

        span.textContent = selected ? selected.text : '';

        return;
    }

    span.textContent = input.value;

    renderSamplePreview(input, output => {
        span.textContent = output;
    });
};

const applySpanVisibility = (span, input) => {
    const field = input.closest('[data-revealed-by]');

    if (span && field) {
        span.hidden = field.hidden;
    }
};

const applyPauseIndicator = (actionPreview, input) => {
    if (!input.name.includes('waitForCompletion')) {
        return;
    }

    const pauseIndicator      = actionPreview?.querySelector('.pause-indicator');
    const backgroundIndicator = actionPreview?.querySelector('.background-indicator');

    if (pauseIndicator && backgroundIndicator) {
        pauseIndicator.hidden      = !input.checked;
        backgroundIndicator.hidden = input.checked;
    }
};

const keyParameters = (action) => action.querySelector('[data-key-parameter]');

const isKeyParameter = (input) => input.closest('[data-key-parameter]') !== null;

const applyKeyLabel = (span, parameters) => {
    const template = parameters?.dataset.labelTemplate;

    if (!span || !template) {
        return;
    }

    const values = [...parameters.querySelectorAll(':is(select, input)')]
        .filter(field => !field.matches('od-select .search input'))
        .map(field => field.value.trim());
    let index    = 0;

    span.textContent = template.replace(/%[ds]/g, () => values[index++] || '#');
};

const actionInputs = (action) => [...action.querySelectorAll('.automation-action-contents :is(select, input, textarea)[name]')]
    .filter(input => !input.matches('od-select .search input'));

const spanInputs = (action) => actionInputs(action)
    .filter(input => input.type !== 'checkbox' && !isKeyParameter(input))
;

const synchronizeActionInput = (action, actionPreview, input) => {
    const index = spanInputs(action).indexOf(input);

    if (index === -1) {
        return;
    }

    const span = actionPreview.querySelector(`span:nth-of-type(${index + 1})`);

    applySpanValue(span, input);
    applySpanVisibility(span, input);

    if (input.name === 'key') {
        applyKeyLabel(span, keyParameters(action));
    }
};

const synchronizeActionFields = (action, actionPreview) => {
    actionInputs(action).forEach((input) => {
        if (input.type === 'checkbox') {
            applyPauseIndicator(actionPreview, input);
        }
    });

    spanInputs(action).forEach(input => synchronizeActionInput(action, actionPreview, input));
};

export const removeActionPreview = async (event) => {
    const preview = document.querySelector('.workflow-container .automation.preview');

    if (!preview) {
        return;
    }

    const actionPreview = preview.querySelectorAll('.actions ol li')[event.detail.index];

    if (!actionPreview) {
        return;
    }

    await animateByClass(actionPreview, 'remove-action');

    actionPreview.remove();

    updateActionLabel(preview);
};

export const addActionPreview = (event) => {
    const newActionPreview = document.createElement('li');

    newActionPreview.appendChild(event.detail.action.querySelector('template').content);

    newActionPreview.querySelectorAll('span').forEach(span => {
        if (span.textContent === '%s') {
            span.textContent = '...';
        }
    });

    synchronizeActionFields(event.detail.action, newActionPreview);

    animateByClass(newActionPreview, 'add-action');

    const automation = document.querySelector('.workflow-container .automation.preview');

    automation.querySelector('.actions ol').append(newActionPreview);

    updateActionLabel(automation);
};

const syncTitle = (preview, target) => {
    const title = preview.querySelector('h3');

    if (target.value.trim() === '') {
        title.classList.add('untitled');

        title.textContent = title.dataset.untitledContent

        return;
    }

    title.classList.remove('untitled');

    title.textContent = target.value;
};

const previewFor = (preview, action) => {
    const actionIndex = Array.from(action.parentNode.querySelectorAll('.automation-action')).indexOf(action);

    return preview.querySelectorAll('.actions ol li')[actionIndex];
};

const syncAction = (preview, action, target) => {
    const actionPreview = previewFor(preview, action);

    if (isKeyParameter(target)) {
        synchronizeActionInput(action, actionPreview, action.querySelector('[name="key"]'));

        return;
    }

    if (target.type === 'checkbox') {
        applyPauseIndicator(actionPreview, target);

        return;
    }

    synchronizeActionInput(action, actionPreview, target);

    action
        .querySelectorAll(
            `[data-revealed-by="${CSS.escape(target.name)}"] :is(select, input, textarea)[name]`,
        )
        .forEach(input => synchronizeActionInput(action, actionPreview, input))
    ;
};

export const refreshActionPreview = (action) => {
    const preview = document.querySelector('.workflow-container .automation.preview');
    const actionPreview = preview ? previewFor(preview, action) : null;

    if (actionPreview) {
        synchronizeActionFields(action, actionPreview);
    }
};

export const refreshActionPreviews = (form) => {
    const preview = document.querySelector('.workflow-container .automation.preview');

    if (!preview) {
        return;
    }

    const actionPreviews = preview.querySelectorAll('.actions ol li');

    form.querySelectorAll('.automation-action').forEach((action, index) => {
        if (actionPreviews[index]) {
            synchronizeActionFields(action, actionPreviews[index]);
        }
    });
};

export const synchronizeOnInput = (event) => {
    if (event.target.matches('od-select .search input')) {
        return;
    }

    const preview = document.querySelector('.workflow-container .automation.preview');

    const action = event.target.closest('.automation-action');

    if (action) {
        syncAction(preview, action, event.target);

        return;
    }

    if (event.target.getAttribute('name') === 'title') {
        syncTitle(preview, event.target);
    }
};
